<template>
  <NuxtLink
    :to="$localeRoute(service.slug && service.partner.slug ? `/p/${service.partner.slug}/s/${service.slug}` : `/services/${service._id}`)"
    class="group flex flex-col rounded-3xl border border-border/50 bg-white p-4 transition-transform hover:scale-[1.005] active:scale-[0.99]"
    @click="captureEvent('search_result_clicked', {
      serviceId: service._id,
      partnerId: service.partnerId,
      serviceType: service.serviceType
    })"
  >
    <!-- Header row: title + price pill -->
    <div class="flex items-start justify-between gap-3 pb-4">
      <div class="min-w-0">
        <h3 class="text-lg font-bold leading-tight text-foreground line-clamp-2">
          {{ service.title }}
        </h3>
        <p class="mt-0.5 text-sm text-muted-foreground">
          {{ $ts('servicesSearch.card.at') }} {{ service.partner.businessName }}
          <Icon
            v-if="service.partner.isVerified"
            name="tabler:shield-check"
            class="inline-block align-[-2px] text-foreground"
            size="14"
          />
        </p>
      </div>
      <span
        v-if="service.price != null"
        class="shrink-0 inline-flex h-11 items-center rounded-full bg-muted px-4 text-sm font-semibold text-foreground"
      >
        {{ formatCurrency(service.price, undefined, service.currency || 'EUR') }}
      </span>
    </div>

    <!-- Image area — gradient + icon (no real image field yet) -->
    <div class="relative overflow-hidden rounded-2xl">
      <!-- Future: real image when SearchServiceItem gains imageUrl -->
      <img
        v-if="service.imageUrl"
        :src="service.imageUrl"
        :alt="service.title"
        class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      <div
        v-else
        :class="['flex aspect-[16/9] items-center justify-center', serviceGradient]"
      >
        <Icon
          :name="serviceIcon"
          class="text-white/80 transition-transform duration-500 group-hover:scale-110"
          size="56"
        />
      </div>

      <!-- Floating chips -->
      <!-- Top-left: availability -->
      <span
        v-if="service.nextAvailability"
        :class="[
          'absolute left-2.5 top-2.5 inline-flex items-center rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold shadow-sm',
          service.nextAvailability === 'available_today' ? 'text-green-700' :
          service.nextAvailability === 'tomorrow' ? 'text-amber-700' :
          'text-blue-700'
        ]"
      >
        {{ service.nextAvailability === 'available_today' ? $ts('servicesSearch.availability.available_today') : service.nextAvailability === 'tomorrow' ? $ts('servicesSearch.availability.tomorrow') : $ts('servicesSearch.availability.this_week') }}
      </span>

      <!-- Bottom-right: rating + distance -->
      <span
        v-if="service.location?.averageRating || service.distance != null"
        class="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold shadow-sm"
      >
        <template v-if="service.location?.averageRating">
          <Icon name="material-symbols:kid-star" class="text-foreground" size="14" />
          <span class="text-foreground">{{ service.location.averageRating }}</span>
          <span class="text-muted-foreground">({{ service.location?.reviewCount ?? 0 }})</span>
        </template>
        <template v-if="service.location?.averageRating && service.distance != null">
          <span class="text-muted-foreground/60">&middot;</span>
        </template>
        <span v-if="service.distance != null" class="text-muted-foreground">{{ formattedDistance }}</span>
      </span>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '#imports';
import { usePostHog } from '#composables/usePostHog';
import { formatCurrency } from '#bubba/shared/utils/currency';
import { getServiceTypeIcon } from '#bubba/shared/utils/serviceTypes';
import type { SearchServiceItem } from '#bubba/shared/types/search';

const { $localeRoute, $ts } = useI18n();
const { captureEvent } = usePostHog();

const props = defineProps<{
  service: SearchServiceItem;
}>();

const formattedDistance = computed(() => {
  if (props.service.distance == null) return '';
  if (props.service.distance < 1) return `${Math.round(props.service.distance * 1000)} m`;
  return `${props.service.distance.toFixed(1)} km`;
});

const serviceIcon = computed(() => getServiceTypeIcon(props.service.serviceType));

const serviceGradient = computed(() => {
  const gradientMap: Record<string, string> = {
    grooming: 'bg-gradient-to-br from-orange-400 via-orange-300 to-amber-200',
    walking: 'bg-gradient-to-br from-emerald-500 via-emerald-400 to-green-300',
    daycare: 'bg-gradient-to-br from-amber-400 via-yellow-300 to-yellow-200',
    boarding: 'bg-gradient-to-br from-blue-500 via-blue-400 to-sky-300',
    petsitting: 'bg-gradient-to-br from-rose-400 via-pink-300 to-rose-200',
    training: 'bg-gradient-to-br from-purple-500 via-purple-400 to-violet-300',
    transport: 'bg-gradient-to-br from-slate-500 via-slate-400 to-slate-300',
    vet: 'bg-gradient-to-br from-teal-500 via-teal-400 to-emerald-300',
    online_vet: 'bg-gradient-to-br from-cyan-500 via-cyan-400 to-sky-300',
  };
  return gradientMap[props.service.serviceType] ?? 'bg-gradient-to-br from-gray-400 to-gray-300';
});
</script>
